import {
	Component,
	computed,
	effect,
	inject,
	input,
	signal,
} from '@angular/core';
import { TranslocoModule } from '@ngneat/transloco';
import {
	AppSubjectEnum,
	ModulesEnum,
	StatusTrackingDto,
	StatusTrackingScopeEnum,
} from '@splitsecondconnect/ultramarine-shared';

import { FEATURES } from '../../../../../../../../core/features/features';
import {
	StateBadgeComponent,
	StateBadgeEnum,
} from '../../../../../../../../shared/components/state-badge/state-badge.component';
import { UltraToolTipDirective } from '../../../../../../../../shared/directives/ulta-tooltip.directive';
import { VehicleInsurancesLinkPillComponent } from '../../../../../../vehicle-insurances/components/vehicle-insurances-link-pill/vehicle-insurances-link-pill.component';
import { VehicleInsurancesService } from '../../../../../../vehicle-insurances/vehicle-insurances.service';
import { VehicleRoadTaxesLinkPillComponent } from '../../../../../../vehicle-road-taxes/vehicle-road-taxes-record/components/vehicle-road-taxes-link-pill/vehicle-road-taxes-link-pill.component';
import { VehicleRoadTaxesService } from '../../../../../../vehicle-road-taxes/vehicle-road-taxes.service';
import { VehicleRoadWorthyChecksLinkPillComponent } from '../../../../../../vehicle-road-worthy-checks/components/vehicle-road-worthy-checks-link-pill/vehicle-road-worthy-checks-link-pill.component';
import { VehicleRoadWorthyChecksService } from '../../../../../../vehicle-road-worthy-checks/vehicle-road-worthy-checks.service';
import { VehicleRecordModel } from '../../../../../models/vehicle-record.model';

@Component({
	selector: 'app-vehicle-links-status',
	templateUrl: 'vehicle-links-status.component.html',
	imports: [
		VehicleRoadWorthyChecksLinkPillComponent,
		VehicleInsurancesLinkPillComponent,
		VehicleRoadTaxesLinkPillComponent,
		TranslocoModule,
		StateBadgeComponent,
		UltraToolTipDirective,
	],
	styleUrls: ['vehicle-links-status.component.scss'],
})
export class VehicleLinksStatusComponent<T> {
	features = FEATURES;
	moduleEnum = ModulesEnum;
	stateBadgeEnum = StateBadgeEnum;
	recordData = input<VehicleRecordModel>();

	#roadWorthyChecksService = inject(VehicleRoadWorthyChecksService);
	#insurancesService = inject(VehicleInsurancesService);
	#roadTaxesService = inject(VehicleRoadTaxesService);

	// Signals to store codes for each subject
	roadWorthyCheckCodes = signal<Record<number, string>>({});
	insuranceCodes = signal<Record<number, string>>({});
	roadTaxCodes = signal<Record<number, string>>({});

	statusTracking = input<StatusTrackingDto<T>[]>(null);
	statusTrackingFiltered = computed(() => {
		return (
			this.statusTracking()?.filter(
				(item) => item.scope === StatusTrackingScopeEnum.LINK,
			) ?? []
		);
	});

	// Track previous state to avoid unnecessary API calls
	#previousStatusTrackingIds = signal<Set<number>>(new Set());

	constructor() {
		effect(() => {
			const currentIds = new Set(
				this.statusTrackingFiltered().map((item) => item.sourceId),
			);
			const previousIds = this.#previousStatusTrackingIds();

			// Check if the source IDs have actually changed
			const hasChanged =
				currentIds.size !== previousIds.size ||
				[...currentIds].some((id) => !previousIds.has(id));

			if (!hasChanged) {
				return; // No changes, skip API calls
			}

			// Update the previous state
			this.#previousStatusTrackingIds.set(currentIds);

			// Road Worthy Checks
			this.statusTrackingFiltered()
				.filter(
					(item) =>
						item.sourceSubject === AppSubjectEnum.VEHICLE_ROAD_WORTHY_CHECK,
				)
				.forEach((item) => {
					this.#roadWorthyChecksService
						.getSearchResults('', [item.sourceId])
						.subscribe((res) => {
							if (res.data && res.data[0]) {
								this.roadWorthyCheckCodes.update((prev) => ({
									...prev,
									[item.sourceId]: res.data[0].code,
								}));
							}
						});
				});

			// Insurances
			this.statusTrackingFiltered()
				.filter(
					(item) => item.sourceSubject === AppSubjectEnum.VEHICLE_INSURANCE,
				)
				.forEach((item) => {
					this.#insurancesService
						.getSearchResults('', [item.sourceId])
						.subscribe((res) => {
							if (res.data && res.data[0]) {
								this.insuranceCodes.update((prev) => ({
									...prev,
									[item.sourceId]: res.data[0].code,
								}));
							}
						});
				});

			// Road Taxes
			this.statusTrackingFiltered()
				.filter(
					(item) => item.sourceSubject === AppSubjectEnum.VEHICLE_ROAD_TAX,
				)
				.forEach((item) => {
					this.#roadTaxesService
						.getSearchResults('', [item.sourceId])
						.subscribe((res) => {
							if (res.data && res.data[0]) {
								this.roadTaxCodes.update((prev) => ({
									...prev,
									[item.sourceId]: res.data[0].code,
								}));
							}
						});
				});
		});
	}

	roadWorthyChecks = computed(() => {
		return this.statusTrackingFiltered()
			.filter(
				(item) =>
					item.sourceSubject === AppSubjectEnum.VEHICLE_ROAD_WORTHY_CHECK,
			)
			.map((item) => ({
				sourceId: item.sourceId,
				code: this.roadWorthyCheckCodes()[item.sourceId] ?? '',
			}));
	});

	insurances = computed(() => {
		return this.statusTrackingFiltered()
			.filter((item) => item.sourceSubject === AppSubjectEnum.VEHICLE_INSURANCE)
			.map((item) => ({
				sourceId: item.sourceId,
				code: this.insuranceCodes()[item.sourceId] ?? '',
			}));
	});

	roadtaxes = computed(() => {
		return this.statusTrackingFiltered()
			.filter((item) => item.sourceSubject === AppSubjectEnum.VEHICLE_ROAD_TAX)
			.map((item) => ({
				sourceId: item.sourceId,
				code: this.roadTaxCodes()[item.sourceId] ?? '',
			}));
	});
}
